import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Button } from "@replo/design-system/components/button/Button";
import { Popover } from "@replo/design-system/components/popover/Popover";
import { toast } from "@replo/design-system/components/toast/Toast";
import twMerge from "@replo/design-system/utils/twMerge";
import loadingAnimation from "@v2/components/reploader.json";
import { useDesignLibrary } from "@v2/features/design-library/hooks/useDesignLibrary";
import { useInfiniteFileTemplates } from "@v2/features/home/hooks/useInfiniteFileTemplates";
import LoadingHeader from "@v2/features/page-renderer/components/LoadingHeader";
import PageSelectionDropdown from "@v2/features/page-renderer/components/navbar/PageSelectionDropdown";
import ResponsiveControls from "@v2/features/page-renderer/components/navbar/ResponsiveControls";
import PageRenderer from "@v2/features/page-renderer/components/PageRenderer";
import { useViewportStore } from "@v2/features/page-renderer/store/viewportStore";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useInfinitePages } from "@v2/hooks/useInfinitePages";
import { stylesRouter, stylesUtils } from "@v2/trpc/routes/styles";
import isEqual from "lodash-es/isEqual";
import Lottie from "lottie-react";
import { ChevronDown } from "lucide-react";
import { useBlocker } from "react-router-dom";

import { DesignLibraryColorPresets } from "./DesignLibraryColorPresets";
import { DesignLibraryColors } from "./DesignLibraryColors";
import { DesignLibraryCustomFonts } from "./DesignLibraryCustomFonts";
import { DesignLibraryFontPresets } from "./DesignLibraryFontPresets";
import { DesignLibraryNavigationBlockerModal } from "./DesignLibraryNavigationBlockerModal";
import { DesignLibrarySummary } from "./DesignLibrarySummary";
import { DesignLibraryTypography } from "./DesignLibraryTypography";

export const DesignLibrary: React.FC = () => {
  const projectId = useCurrentProjectId();
  const [isPageSelectionPopoverOpen, setIsPageSelectionPopoverOpen] =
    React.useState(false);
  const [draftStyleLibrary, setDraftStyleLibrary] =
    React.useState<StyleLibrary | null>(null);
  const [isPullingStyles, setIsPullingStyles] = React.useState(false);

  const {
    currentView,
    setCurrentView,
    handleCustomFontsClick,
    handleCustomFontsBack,
  } = useDesignLibrary();
  const { mutateAsync: updateStyleLibrary, isPending } =
    stylesRouter.createOrUpdateStyleLibrary.useMutation();

  const { data: styleLibraryData } = stylesRouter.getStyleLibrary.useQuery(
    { projectId: projectId ?? "" },
    { enabled: Boolean(projectId) },
  );

  const styleLibrary =
    draftStyleLibrary ?? styleLibraryData?.styleLibrary ?? null;

  const { pages } = useInfinitePages({
    projectId: projectId ?? null,
    limit: 12,
  });

  const { templates } = useInfiniteFileTemplates({
    projectId: projectId ?? null,
    type: "page",
    limit: 1,
    enabled: true,
    onlyGlobalTemplates: true,
  });

  const [selectedPageId, setSelectedPageId] = React.useState<string | null>(
    null,
  );
  const hasUnsavedChanges =
    draftStyleLibrary !== null &&
    !isEqual(draftStyleLibrary, styleLibraryData?.styleLibrary ?? null);

  const activePageId = selectedPageId ?? pages?.[0]?.id ?? null;
  const selectedPage = pages?.find((page) => page.id === activePageId) ?? null;

  const firstTemplate = templates?.[0];
  const pageRoute =
    selectedPage?.pageRoute ??
    (firstTemplate ? `/template-preview/${firstTemplate.id}` : "");

  const styleLibraryFontCSS = React.useMemo(() => {
    if (!draftStyleLibrary?.typography) {
      return "";
    }

    return Object.values(draftStyleLibrary.typography)
      .filter((font) => font.name && font.url)
      .map(
        (font) => `
        @font-face {
          font-family: '${font.name}';
          src: url('${font.url}') format('woff2'), url('${font.url}') format('woff');
          font-display: swap;
        }
      `,
      )
      .join("");
  }, [draftStyleLibrary?.typography]);

  const selectedPageName = selectedPage?.name ?? "Landing Page Template";
  const { currentViewport } = useViewportStore();

  const navigationBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      (hasUnsavedChanges || isPullingStyles) &&
      currentLocation.pathname !== nextLocation.pathname
    );
  });

  const handleConfirmModal = async () => {
    navigationBlocker.proceed?.();
  };

  const handleCancelModal = () => {
    navigationBlocker.reset?.();
  };

  if (!projectId) {
    return null;
  }

  return (
    <div className="flex flex-col w-full h-screen overflow-hidden">
      {/* Add font-face styles for StyleLibrary fonts */}
      {styleLibraryFontCSS && (
        <style dangerouslySetInnerHTML={{ __html: styleLibraryFontCSS }} />
      )}

      <PageHeader
        title="Design Library"
        description="Customize your global styles to keep designs on-brand."
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="base"
              onClick={() => {
                setDraftStyleLibrary(null);
              }}
              disabled={!hasUnsavedChanges || isPullingStyles}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              size="base"
              onClick={() => {
                void (async () => {
                  if (!draftStyleLibrary) {
                    return;
                  }

                  await updateStyleLibrary({
                    projectId,
                    styleLibrary: draftStyleLibrary,
                  });
                  await stylesUtils.getStyleLibrary.invalidate({
                    projectId: projectId,
                  });

                  setDraftStyleLibrary(null);
                  toast.success({
                    title: "Style Library Updated",
                    description: "Your library has been updated successfully",
                  });
                })();
              }}
              disabled={!hasUnsavedChanges || isPullingStyles || isPending}
              loading={isPending}
            >
              Save Changes
            </Button>
          </div>
        }
      />

      <div className="flex-1 min-h-0 overflow-hidden pb-6 pt-6">
        <div className="flex flex-col lg:flex-row items-stretch gap-6 h-full px-3 sm:px-0">
          <div className="flex flex-col items-start gap-6 flex-[1_0_0] self-stretch min-h-0">
            {currentView === "summary" && (
              <DesignLibrarySummary
                styleLibrary={styleLibrary}
                onColorsClick={() => setCurrentView("colors")}
                onTypographyClick={() => setCurrentView("typography")}
                onCustomFontsClick={handleCustomFontsClick}
                onStyleLibraryChange={(styleLibrary) => {
                  return setDraftStyleLibrary(styleLibrary);
                }}
                isPullingStyles={isPullingStyles}
                setIsPullingStyles={setIsPullingStyles}
              />
            )}
            {currentView === "colors" && (
              <DesignLibraryColors
                styleLibrary={styleLibrary}
                onBack={() => setCurrentView("summary")}
                onPresetsClick={() => setCurrentView("presets")}
                onStyleLibraryChange={(styleLibrary) => {
                  return setDraftStyleLibrary(styleLibrary);
                }}
              />
            )}
            {currentView === "presets" && (
              <DesignLibraryColorPresets
                styleLibrary={styleLibrary}
                onBack={() => setCurrentView("colors")}
                onStyleLibraryChange={(styleLibrary) => {
                  return setDraftStyleLibrary(styleLibrary);
                }}
              />
            )}
            {currentView === "typography" && (
              <DesignLibraryTypography
                styleLibrary={styleLibrary}
                onBack={() => setCurrentView("summary")}
                onCustomFontsClick={handleCustomFontsClick}
                onFontPresetsClick={() => setCurrentView("fontPresets")}
                onStyleLibraryChange={(styleLibrary) => {
                  return setDraftStyleLibrary(styleLibrary);
                }}
              />
            )}
            {currentView === "fontPresets" && (
              <DesignLibraryFontPresets
                styleLibrary={styleLibrary}
                onBack={() => setCurrentView("typography")}
                onStyleLibraryChange={(styleLibrary) => {
                  return setDraftStyleLibrary(styleLibrary);
                }}
              />
            )}
            {currentView === "customFonts" && (
              <DesignLibraryCustomFonts
                onBack={handleCustomFontsBack}
                onFontDeleted={() => {
                  // TODO (Noah, 2025-10-23): Kinda weird here to reset all changes, what we should
                  // probably do is just fetch the updated style library and merge in any new typography
                  // changes which include the deleted font
                  setDraftStyleLibrary(null);
                }}
              />
            )}
          </div>
          <div
            className={twMerge(
              "flex flex-col items-start gap-4 w-full lg:w-[642px] p-4 rounded-xl border-0.5 border-border bg-white shadow-sm",
              isPullingStyles && "pointer-events-none",
            )}
          >
            <div className="flex justify-between items-start self-stretch">
              <Popover
                open={isPageSelectionPopoverOpen}
                onOpenChange={setIsPageSelectionPopoverOpen}
                showCloseButton={false}
                align="start"
                side="bottom"
                trigger={
                  <div className="flex items-center gap-2 px-2 py-1 rounded-md cursor-pointer transition-colors hover:bg-selectable-hover">
                    <span className="font-semibold line-clamp-1">
                      {selectedPageName}
                    </span>
                    <ChevronDown size={12} />
                  </div>
                }
                UNSAFE_className="max-h-[50vh] w-[90vw] sm:w-[60vw] lg:w-[35vw] overflow-y-auto"
              >
                <PageSelectionDropdown
                  projectId={projectId}
                  currentPageId={selectedPageId ?? undefined}
                  handlePageSelectionCardClick={(page) => {
                    setSelectedPageId(page.id);
                    setIsPageSelectionPopoverOpen(false);
                  }}
                  hideNewPageButton
                />
              </Popover>
              <ResponsiveControls />
            </div>

            <div className="flex-1 w-full overflow-hidden">
              <div
                className={
                  currentViewport === "mobile"
                    ? "w-full max-w-[350px] mx-auto h-full relative"
                    : "w-full h-full relative"
                }
              >
                {isPullingStyles ? (
                  <div className="w-full h-full bg-sheet rounded-lg flex flex-col items-center justify-center">
                    <Lottie
                      animationData={loadingAnimation}
                      loop={true}
                      className="z-10 w-[200px]"
                    />
                    <div className="text-sm text-muted animate-pulse">
                      Loading preview...
                    </div>
                  </div>
                ) : (
                  <>
                    <LoadingHeader pageRoute={pageRoute} key={activePageId} />
                    {pageRoute && (
                      <PageRenderer
                        pageRoute={pageRoute}
                        overrideStyleLibrary={draftStyleLibrary}
                        viewingVersionId={null}
                        className="h-full w-full rounded-lg"
                        isInteractive={true}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DesignLibraryNavigationBlockerModal
        isOpen={navigationBlocker.state === "blocked"}
        onOpenChange={() => {
          if (navigationBlocker.state === "blocked") {
            navigationBlocker.reset?.();
          }
        }}
        onConfirm={handleConfirmModal}
        onCancel={handleCancelModal}
        title={
          isPullingStyles ? "Style extraction in progress" : "Discard changes?"
        }
        description={
          isPullingStyles
            ? "You are extracting styles from a URL. If you leave now, the extraction will be canceled."
            : "You have unsaved Design Library changes. If you leave now, your edits will be lost."
        }
        cancelButtonText={isPullingStyles ? "Stay" : "Cancel"}
        confirmButtonText={isPullingStyles ? "Leave" : "Discard"}
        confirmButtonVariant="destructive"
      />
    </div>
  );
};
